import React from "react";
import Header from "../../components/Header";
import "./CliDocs.css";

type TabId = "setup" | "agent";

interface CliDocsProps {
    darkMode: boolean;
}

/** Full CLI documentation as plain text, structured for AI agent consumption */
const CLI_DOCS_FULL_TEXT = `# Interview Sandbox CLI

Invite candidates, fetch interviews, candidate status, and evaluations from the command line. No dashboard required. Use --json for structured output suitable for scripts and AI agents.

## Install

npm install -g interview-sandbox-cli

## Setup

1. Get an API key from Interview Sandbox dashboard: Settings → API Keys → Create API key
2. Configure via CLI or environment variable:

sandbox config set api-key isk_your_key_here

# Or set environment variable:
export INTERVIEW_SANDBOX_API_KEY=isk_your_key_here
export INTERVIEW_SANDBOX_API_URL=https://your-api-url.com  # optional, override API base URL

## Commands

- sandbox list
  List your interviews. Use --json for JSON output.

- sandbox status --interview <id>
  List candidates for an interview. Use --json for JSON output.

- sandbox evaluate --candidate "Name" --interview <id>
  Detailed evaluation for one candidate. Use --json for JSON output.

- sandbox invite --interview <id> --email <email>
  Send email invitation to a candidate. Optional: --name "Name", --expires 7.

- sandbox invites --interview <id>
  List invites for an interview. Use --json for JSON output.

- sandbox config set api-key <key>
  Store API key.

- sandbox config get api-key
  Show masked key.

- sandbox config path
  Show config file path.

## Options

--interview, -i <id>    Interview ID (required for status, evaluate, invite, invites)
--candidate, -c <name>  Candidate name (required for evaluate)
--email, -e <email>     Candidate email (required for invite)
--name, -n <name>       Candidate name (optional for invite)
--expires <days>        Invite expiry in days (default 7, for invite)
--api-url <url>         Override API base URL
--json, -j              Output JSON for scripts and agents

## For AI Agents

Use --json on all commands to get structured output. Prefer INTERVIEW_SANDBOX_API_KEY environment variable for authentication.

### Example commands with JSON output

sandbox list --json
sandbox status --interview <interview_id> --json
sandbox invite --interview <id> --email candidate@example.com --name "Jane Smith" --expires 7
sandbox invites --interview <id> --json
sandbox evaluate --candidate "<candidate_name>" --interview <interview_id> --json

### Response schema: sandbox list --json

{
  "interviews": [
    { "id": "abc123", "title": "Senior Frontend", "status": "active" }
  ]
}

### Response schema: sandbox status --interview <id> --json

{
  "totalCandidates": 5,
  "completedInterviews": 3,
  "averageScore": 78,
  "averageTime": 45,
  "candidates": [
    {
      "id": "cand_1",
      "name": "Jane Smith",
      "email": "alex@example.com",
      "score": 82,
      "timeSpent": 48,
      "status": "completed",
      "completedAt": "2024-01-15T14:30:00Z"
    }
  ]
}

### Response schema: sandbox evaluate --candidate "Name" --interview <id> --json

Coding interviews include: problemUnderstanding, approach, implementation, complexityAnalysis, communication, recovery.
System design interviews include: requirementsClarification, architectureDesign, scalability, tradeOffs, diagramQuality, communication.
Evidence fields (e.g. problemUnderstandingEvidence) may be present for auditability.

{
  "candidate": { "name": "Jane Smith", "id": "cand_1" },
  "interviewId": "abc123",
  "scores": {
    "problemUnderstanding": 4,
    "approach": 5,
    "implementation": 4,
    "complexityAnalysis": 3,
    "communication": 5,
    "recovery": 4,
    "overall": 82
  },
  "summary": "Strong problem-solving skills...",
  "biasAudit": "PASSED"
}

### Error response (when --json is used)

{
  "error": "Not authenticated",
  "message": "Run: sandbox config set api-key <key>"
}

### Typical agent workflow

1. Set INTERVIEW_SANDBOX_API_KEY in the environment
2. Run sandbox list --json to get interview IDs
3. Run sandbox invite --interview <id> --email <email> to send invites (or use dashboard agent)
4. Run sandbox status --interview <id> --json to get candidates for an interview
5. Run sandbox evaluate --candidate "Name" --interview <id> --json for detailed scores and summary
`;

function CodeBlock({ children, className = "" }: { children: string; className?: string }): React.ReactElement {
    const [copied, setCopied] = React.useState(false);

    const handleCopy = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(children);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Fallback for older browsers
            const textarea = document.createElement("textarea");
            textarea.value = children;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div className="cli-docs-code-wrapper">
            <pre className={`cli-docs-code ${className}`}>
                <code>{children}</code>
            </pre>
            <button
                type="button"
                className={`cli-docs-copy-btn ${copied ? "cli-docs-copy-btn-copied" : ""}`}
                onClick={handleCopy}
                aria-label="Copy to clipboard"
                title="Copy to clipboard"
            >
                {copied ? "Copied!" : "Copy"}
            </button>
        </div>
    );
}

export default function CliDocs({ darkMode }: CliDocsProps): React.ReactElement {
    const [activeTab, setActiveTab] = React.useState<TabId>("setup");
    const [fullCopied, setFullCopied] = React.useState(false);

    const handleCopyFull = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(CLI_DOCS_FULL_TEXT);
            setFullCopied(true);
            setTimeout(() => setFullCopied(false), 2000);
        } catch {
            const textarea = document.createElement("textarea");
            textarea.value = CLI_DOCS_FULL_TEXT;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
            setFullCopied(true);
            setTimeout(() => setFullCopied(false), 2000);
        }
    };

    return (
        <>
            <Header darkMode={darkMode} />
            <div className={`cli-docs ${darkMode ? "cli-docs-dark" : ""}`}>
                <div className="cli-docs-container">
                    <div className="cli-docs-header-row">
                        <h1 className="cli-docs-title">Interview Sandbox CLI</h1>
                        <button
                            type="button"
                            className={`cli-docs-copy-full-btn ${fullCopied ? "cli-docs-copy-btn-copied" : ""}`}
                            onClick={handleCopyFull}
                            aria-label="Copy full documentation for AI agents"
                            title="Copy full documentation (formatted for AI agents)"
                        >
                            {fullCopied ? "Copied!" : "Copy full docs"}
                        </button>
                    </div>
                    <p className="cli-docs-subtitle">
                        Fetch interviews, candidate status, and evaluations from the command line — no dashboard required.
                    </p>

                    <div className="cli-docs-tabs">
                        <button
                            className={`cli-docs-tab ${activeTab === "setup" ? "cli-docs-tab-active" : ""}`}
                            onClick={() => setActiveTab("setup")}
                        >
                            Setup
                        </button>
                        <button
                            className={`cli-docs-tab ${activeTab === "agent" ? "cli-docs-tab-active" : ""}`}
                            onClick={() => setActiveTab("agent")}
                        >
                            For AI Agents
                        </button>
                    </div>

                    {activeTab === "setup" && (
                        <div className="cli-docs-content">
                            <section>
                                <h2>Install</h2>
                                <pre className="cli-docs-code">
                                    <code>npm install -g interview-sandbox-cli</code>
                                </pre>
                            </section>

                            <section>
                                <h2>Get your API key</h2>
                                <ol className="cli-docs-list">
                                    <li>Log in to your Interview Sandbox dashboard</li>
                                    <li>Go to <strong>Settings → API Keys</strong></li>
                                    <li>Click <strong>Create API key</strong></li>
                                    <li>Copy the key (starts with <code>isk_</code>)</li>
                                </ol>
                            </section>

                            <section>
                                <h2>Configure</h2>
                                <pre className="cli-docs-code">
                                    <code>sandbox config set api-key isk_your_key_here</code>
                                </pre>
                                <p className="cli-docs-note">
                                    Or use the <code>INTERVIEW_SANDBOX_API_KEY</code> environment variable.
                                </p>
                            </section>

                            <section>
                                <h2>Commands</h2>
                                <table className="cli-docs-table">
                                    <thead>
                                        <tr>
                                            <th>Command</th>
                                            <th>Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><code>sandbox list</code></td>
                                            <td>List your interviews</td>
                                        </tr>
                                        <tr>
                                            <td><code>sandbox status --interview &lt;id&gt;</code></td>
                                            <td>List candidates for an interview</td>
                                        </tr>
                                        <tr>
                                            <td><code>sandbox evaluate --candidate "Name" --interview &lt;id&gt;</code></td>
                                            <td>Detailed evaluation for one candidate</td>
                                        </tr>
                                        <tr>
                                            <td><code>sandbox invite --interview &lt;id&gt; --email &lt;email&gt;</code></td>
                                            <td>Send email invite to a candidate</td>
                                        </tr>
                                        <tr>
                                            <td><code>sandbox invites --interview &lt;id&gt;</code></td>
                                            <td>List invites for an interview</td>
                                        </tr>
                                        <tr>
                                            <td><code>sandbox config set api-key &lt;key&gt;</code></td>
                                            <td>Store API key</td>
                                        </tr>
                                        <tr>
                                            <td><code>sandbox config get api-key</code></td>
                                            <td>Show masked key</td>
                                        </tr>
                                        <tr>
                                            <td><code>sandbox config path</code></td>
                                            <td>Show config file path</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </section>

                            <section>
                                <h2>Options</h2>
                                <ul className="cli-docs-list">
                                    <li><code>--interview</code>, <code>-i</code> — Interview ID (required for status/evaluate/invite/invites)</li>
                                    <li><code>--candidate</code>, <code>-c</code> — Candidate name (required for evaluate)</li>
                                    <li><code>--email</code>, <code>-e</code> — Candidate email (required for invite)</li>
                                    <li><code>--name</code>, <code>-n</code> — Candidate name (optional for invite)</li>
                                    <li><code>--expires</code> — Invite expiry in days (default 7, for invite)</li>
                                    <li><code>--api-url</code> — Override API URL</li>
                                    <li><code>--json</code>, <code>-j</code> — Output JSON for scripts</li>
                                </ul>
                            </section>
                        </div>
                    )}

                    {activeTab === "agent" && (
                        <div className="cli-docs-content">
                            <section>
                                <h2>Overview</h2>
                                <p>
                                    The Interview Sandbox CLI is designed for automation. Use <code>--json</code> on any command
                                    to get structured JSON output for parsing, pipelines, or AI agent consumption.
                                </p>
                            </section>

                            <section>
                                <h2>Authentication</h2>
                                <p>Preferred: set the API key via environment variable:</p>
                                <CodeBlock>export INTERVIEW_SANDBOX_API_KEY=isk_your_key_here</CodeBlock>
                                <p>Optional: override the API base URL:</p>
                                <CodeBlock>export INTERVIEW_SANDBOX_API_URL=https://your-api-url.com</CodeBlock>
                            </section>

                            <section>
                                <h2>JSON output</h2>
                                <p>Append <code>--json</code> or <code>-j</code> to any command:</p>
                                <CodeBlock>{`sandbox list --json
sandbox status --interview abc123 --json
sandbox invite --interview abc123 --email jane@example.com --name "Jane Smith"
sandbox evaluate --candidate "Jane Smith" --interview abc123 --json`}</CodeBlock>
                            </section>

                            <section>
                                <h2>Response schemas</h2>
                                <h3>sandbox list --json</h3>
                                <CodeBlock className="cli-docs-json">{`{
  "interviews": [
    { "id": "abc123", "title": "Senior Frontend", "status": "active" }
  ]
}`}</CodeBlock>
                            </section>

                            <section>
                                <h3>sandbox status --interview &lt;id&gt; --json</h3>
                                <CodeBlock className="cli-docs-json">{`{
  "totalCandidates": 5,
  "completedInterviews": 3,
  "averageScore": 78,
  "averageTime": 45,
  "candidates": [
    {
      "id": "cand_1",
      "name": "Jane Smith",
      "email": "alex@example.com",
      "score": 82,
      "timeSpent": 48,
      "status": "completed",
      "completedAt": "2024-01-15T14:30:00Z"
    }
  ]
}`}</CodeBlock>
                            </section>

                            <section>
                                <h3>sandbox evaluate --candidate "Name" --interview &lt;id&gt; --json</h3>
                                <CodeBlock className="cli-docs-json">{`{
  "candidate": { "name": "Jane Smith", "id": "cand_1" },
  "interviewId": "abc123",
  "scores": {
    "problemUnderstanding": 4,
    "approach": 5,
    "implementation": 4,
    "complexityAnalysis": 3,
    "communication": 5,
    "recovery": 4,
    "overall": 82
  },
  "summary": "Strong problem-solving skills...",
  "biasAudit": "PASSED"
}`}</CodeBlock>
                            </section>

                            <section>
                                <h2>Error handling</h2>
                                <p>Errors are returned as JSON when <code>--json</code> is used:</p>
                                <CodeBlock className="cli-docs-json">{`{
  "error": "Not authenticated",
  "message": "Run: sandbox config set api-key <key>"
}`}</CodeBlock>
                            </section>

                            <section>
                                <h2>Typical agent workflow</h2>
                                <ol className="cli-docs-list">
                                    <li>Set <code>INTERVIEW_SANDBOX_API_KEY</code> in the environment</li>
                                    <li>Run <code>sandbox list --json</code> to get interview IDs</li>
                                    <li>Run <code>sandbox invite --interview &lt;id&gt; --email &lt;email&gt;</code> to send invites (or use dashboard agent)</li>
                                    <li>Run <code>sandbox status --interview &lt;id&gt; --json</code> to get candidates</li>
                                    <li>Run <code>sandbox evaluate --candidate "Name" --interview &lt;id&gt; --json</code> for detailed scores</li>
                                </ol>
                            </section>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
